import React, { useCallback, useEffect, useRef } from "react";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { getRecorderTotalListForUserGroup } from "api/recorderAPI";
import {
  Recorder,
  RecorderTotalListForUserGroupSystem,
} from "api/interfaces/recorderInterface.interface";
import {
  UserGroupCURequest,
  UserGroupCUSystem,
  UserGroupSystemCamera,
} from "api/interfaces/userInterface.interface";
import {
  VirtualExpandTable,
  VirtualTable,
} from "components/atoms/table/AntdVirtualTable";
import { Checkbox, TableColumnsType } from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import { TableProps } from "antd/lib";
import { formatNumber, matchStringSearchKeyword } from "utils/functions";
import styled, { css } from "styled-components";
import * as mediaQuery from "components/MediaQuery";
import type { Reference } from "rc-table";
import CameraList from "./CameraList";
import { calcRem } from "styles/theme";
import { FLEX } from "styles/flex";
import {
  checkCovaPermissionFirmwareVersion,
  checkTypeIsCovaOrVmax,
} from "utils/RecorderUtil";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

type SearchRecorderProps = {
  accountId: string;
  onSelectedRowsChange: (
    selectedRows: NonNullable<UserGroupCURequest["systems"]>
  ) => void;
  onChangeCamera: (system: UserGroupCUSystem) => void;
  selectedRow?: UserGroupCURequest["systems"];
  search: string;
  isDisabled: boolean;
  userId: string;
};

const isCameraExpandableSystem = (
  system: RecorderTotalListForUserGroupSystem
): boolean => {
  return checkTypeIsCovaOrVmax(system) && (system.cameras ?? []).length > 0;
};

const checkIsExpandSystem = (
  system: RecorderTotalListForUserGroupSystem
): boolean => {
  if (system.displayType === "Spectrum" && system.recorders) {
    return true;
  }

  return isCameraExpandableSystem(system);
};

const areAllCamerasSelected = (
  cameras?: UserGroupCUSystem["cameras"]
): boolean => {
  return (
    (cameras?.length ?? 0) > 0 && cameras!.every((camera) => camera.isAccess)
  );
};

const getSelectableCameras = (cameras?: UserGroupSystemCamera[]) => {
  return (cameras ?? []).filter((camera) => !camera.isDisabled);
};

const toUserGroupSystemPayload = (
  row: RecorderTotalListForUserGroupSystem
): UserGroupCUSystem => {
  if (!isCameraExpandableSystem(row)) {
    return {
      mergedSystemId: row.mergedSystemId,
      displayType: row.displayType,
      allCameras: true,
    };
  }

  const selectableCameras = getSelectableCameras(row.cameras);

  const cameras = selectableCameras.map((camera) => ({
    channel: camera.channel,
    cameraId: camera.cameraId,
    isAccess: true,
    cameraUuid: camera.cameraUuid,
  }));

  return {
    mergedSystemId: row.mergedSystemId,
    displayType: row.displayType,
    allCameras: areAllCamerasSelected(cameras),
    cameras,
  };
};

export default function UserGroupRecorderList(
  props: SearchRecorderProps
): JSX.Element {
  const isMobile = mediaQuery.useIsMobile();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const {
    isDisabled,
    onChangeCamera,
    onSelectedRowsChange,
    search,
    selectedRow,
  } = props;

  const tableRef = useRef<Reference>(null);

  const { data, isLoading, refetch } = useQuery(
    ["recorderListUserGroup", props.accountId, props.userId],
    () =>
      getRecorderTotalListForUserGroup({
        accountId: props.accountId,
        userId: props.userId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );

  const onSelectChange = useCallback(
    (
      newSelectedRowKeys: React.Key[],
      selectedRows: RecorderTotalListForUserGroupSystem[]
    ) => {
      setSelectedRowKeys(newSelectedRowKeys);
      onSelectedRowsChange(
        selectedRows.map((row: RecorderTotalListForUserGroupSystem) => {
          const selectedSystem = selectedRow?.find(
            (system) => system.mergedSystemId === row.mergedSystemId
          );

          if (!selectedSystem) {
            return toUserGroupSystemPayload(row);
          }

          return {
            ...selectedSystem,
            allCameras: areAllCamerasSelected(selectedSystem.cameras),
          };
        })
      );
    },
    [onSelectedRowsChange, selectedRow]
  );
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const buildSelectedSystems = useCallback(
    (
      nextSelectedRowKeys: React.Key[],
      overrides?: Record<string, UserGroupCUSystem>
    ): UserGroupCUSystem[] => {
      const allSystems = data?.result ?? [];

      return allSystems
        .filter((row: RecorderTotalListForUserGroupSystem) =>
          nextSelectedRowKeys.includes(row.mergedSystemId)
        )
        .map((row: RecorderTotalListForUserGroupSystem) => {
          const overrideSystem = overrides?.[row.mergedSystemId];

          if (overrideSystem) {
            return overrideSystem;
          }

          const selectedSystem = selectedRow?.find(
            (system) => system.mergedSystemId === row.mergedSystemId
          );

          if (!selectedSystem) {
            return toUserGroupSystemPayload(row);
          }

          return {
            ...selectedSystem,
            allCameras: areAllCamerasSelected(selectedSystem.cameras),
          };
        });
    },
    [data?.result, selectedRow]
  );

  const isPartialSystemSelected = useCallback(
    (record: RecorderTotalListForUserGroupSystem) => {
      const selectedSystem = selectedRow?.find(
        (system) => system.mergedSystemId === record.mergedSystemId
      );

      if (!selectedSystem?.cameras?.length) return false;

      const selectedCount = selectedSystem.cameras.filter(
        (camera) => camera.isAccess
      ).length;

      return selectedCount > 0 && !selectedSystem.allCameras;
    },
    [selectedRow]
  );

  const getSystemPayload = useCallback(
    (row: RecorderTotalListForUserGroupSystem): UserGroupCUSystem =>
      toUserGroupSystemPayload(row),
    []
  );

  const syncSelectedSystems = useCallback(
    (
      nextSelectedRowKeys: React.Key[],
      overrides?: Record<string, UserGroupCUSystem>
    ) => {
      const nextSelectedSystems = buildSelectedSystems(
        nextSelectedRowKeys,
        overrides
      );

      setSelectedRowKeys(nextSelectedRowKeys);
      onSelectedRowsChange(nextSelectedSystems);
    },
    [buildSelectedSystems, onSelectedRowsChange]
  );

  const rowSelection: TableRowSelection<RecorderTotalListForUserGroupSystem> = {
    columnWidth: 30,
    selectedRowKeys,
    onChange: onSelectChange,
    type: "checkbox",
    preserveSelectedRowKeys: true,
    renderCell: (_, record) => {
      const isPartial = isPartialSystemSelected(record);
      const isChecked =
        selectedRowKeys.includes(record.mergedSystemId) && !isPartial;

      return (
        <Checkbox
          checked={isChecked}
          indeterminate={isPartial}
          disabled={isDisabled}
          onChange={(e) => {
            if (e.target.checked) {
              const nextSelectedRowKeys = Array.from(
                new Set([...selectedRowKeys, record.mergedSystemId])
              );
              const nextSystem = getSystemPayload(record);
              onChangeCamera(nextSystem);
              syncSelectedSystems(nextSelectedRowKeys, {
                [record.mergedSystemId]: nextSystem,
              });
              return;
            }

            syncSelectedSystems(
              selectedRowKeys.filter((key) => key !== record.mergedSystemId)
            );
          }}
        />
      );
    },
    getCheckboxProps: (record: RecorderTotalListForUserGroupSystem) => {
      return {
        disabled: isDisabled,
      };
    },
  };

  const expandedRowRender = useMemo(
    () => (data: Recorder[]) => {
      return (
        <VirtualExpandTable
          columns={expandSpectrumColumns}
          dataSource={data}
          showHeader={false}
        />
      );
    },
    [selectedRowKeys]
  );

  const dataSource: RecorderTotalListForUserGroupSystem[] = useMemo(() => {
    if (data?.result && data.error === 0) {
      return data.result.filter((rec: RecorderTotalListForUserGroupSystem) => {
        if (search) {
          const isFiltering =
            matchStringSearchKeyword(rec.mergedSystemName, search) ||
            matchStringSearchKeyword(rec.location, search);
          // 하위 recorder 있을때도 keyword 확인
          if (rec.recorders && rec.recorders.length > 0) {
            const tempSubRowsKeyword = rec.recorders.filter((sr) => {
              return (
                matchStringSearchKeyword(sr.name, search) ||
                matchStringSearchKeyword(sr.location?.location, search)
              );
            });

            return tempSubRowsKeyword.length > 0 || isFiltering;
          }

          return isFiltering;
        }
        return true;
      });
    }
    return [];
  }, [data, search]);

  const expandableProps = useMemo<
    TableProps<RecorderTotalListForUserGroupSystem>["expandable"]
  >(() => {
    return {
      columnWidth: 30,
      expandedRowRender: (record) => {
        if (record.displayType === "Spectrum" && record.recorders) {
          return expandedRowRender(record.recorders);
        }

        if (checkTypeIsCovaOrVmax(record) && record.cameras) {
          const selectedSystem = selectedRow?.find(
            (system) => system.mergedSystemId === record.mergedSystemId
          );

          return (
            <CameraList
              mergedSystemId={record.mergedSystemId}
              displayType={record.displayType}
              firmwareVersion={record.firmwareVersion}
              isDisabled={isDisabled}
              cameraList={record.cameras}
              selectedCameraList={selectedSystem?.cameras}
              onChange={onChangeCamera}
            />
          );
        }
        return undefined;
      },
      rowExpandable: (record) => {
        return checkIsExpandSystem(record);
      },
      expandedRowClassName: (record) =>
        checkIsExpandSystem(record) ? "expander-children-row" : "",
    };
  }, [data, selectedRowKeys]);

  const getSelectedCameraCount = useCallback(
    (row: RecorderTotalListForUserGroupSystem) => {
      const selectedSystem = selectedRow?.find(
        (system) => system.mergedSystemId === row.mergedSystemId
      );

      return (
        selectedSystem?.cameras?.filter((camera) => camera.isAccess).length ?? 0
      );
    },
    [selectedRow]
  );

  const columns: TableColumnsType<RecorderTotalListForUserGroupSystem> =
    useMemo(
      () => [
        {
          key: "mergedSystemName",
          dataIndex: "mergedSystemName",
          title: "Name",
          className: "recorder-name",
          render: (value, row) => (
            <>
              {value}
              <address className="location">{row.location ?? ""}</address>
            </>
          ),
        },
        {
          key: "cameraCount",
          dataIndex: "cameraCount",
          title: "",
          className: "camera-meta-cell",
          render: (value, row) => {
            if (row.displayType === "Spectrum") {
              return <CameraCountText>{formatNumber(value)}</CameraCountText>;
            }

            const cameraCount = formatNumber(getSelectedCameraCount(row));
            const total = formatNumber(row.cameras?.length ?? 0);

            if (isMobile) {
              return (
                <CameraCountText>
                  {cameraCount} / {total}
                </CameraCountText>
              );
            }
            return (
              <CameraMetaRow>
                <CameraMetaItem>
                  <span className="meta-label">Selected</span>
                  <span className="meta-value">{cameraCount}</span>
                </CameraMetaItem>
                <CameraMetaItem>
                  <span className="meta-label">Connected</span>
                  <span className="meta-value">{formatNumber(value)}</span>
                </CameraMetaItem>
                <CameraMetaItem>
                  <span className="meta-label">Total</span>
                  <span className="meta-value">{total}</span>
                </CameraMetaItem>
              </CameraMetaRow>
            );
          },
          align: "end",
          width: isMobile ? 80 : 260,
        },
      ],
      [getSelectedCameraCount]
    );

  const expandSpectrumColumns: TableColumnsType<Recorder> = useMemo(
    () => [
      { width: 30 },
      { width: 30 },
      {
        key: "name",
        dataIndex: "name",
        title: "Name",
        className: "recorder-name",
        render: (value, row) => (
          <>
            {value}
            <address className="location">
              {row.location?.location ?? ""}
            </address>
          </>
        ),
      },
      {
        key: "cameraCount",
        dataIndex: "cameraCount",
        title: "",
        render: (value, row) => <CameraCountText>{value}</CameraCountText>,
        align: "end",
        width: 80,
      },
    ],
    [data]
  );

  useEffect(() => {
    if (selectedRow !== undefined) {
      setSelectedRowKeys(selectedRow.map((system) => system.mergedSystemId));
    }
  }, [selectedRow]);

  const rowClassName = useMemo(() => {
    return (record: RecorderTotalListForUserGroupSystem) => {
      if (checkIsExpandSystem(record)) {
        return `multi-expander-row`;
      }
      return "";
    };
  }, [data]);

  return (
    <SearchRecorderWrapper>
      <VirtualTable
        columns={columns}
        dataSource={dataSource}
        rowSelection={rowSelection}
        expandable={expandableProps}
        loading={isLoading}
        rowClassName={rowClassName}
        ref={tableRef}
        rowKey="mergedSystemId"
      />
    </SearchRecorderWrapper>
  );
}

const SearchRecorderWrapper = styled.div`
  .virtual-expand-table {
    padding-right: 0 !important;
  }

  .collapse-button {
    cursor: pointer;
  }

  .ant-table-expanded-row {
    background-color: ${({ theme }) => css`
    rgba(0, 0, 0, ${theme.value === "light" ? 0.02 : 0.2})`};
  }
  ${mediaQuery.isMobile &&
  css`
    .ant-table-row .location {
      opacity: 0.5;
    }
  `}
  ${mediaQuery.isMobile
    ? css`
        .camera-meta-cell {
          padding-right: 18px !important;
        }

        .virtual-expand-table td.ant-table-selection-column {
          text-align: left;
          padding-left: 44px !important;
        }
        .ant-table-selection-column .ant-radio-wrapper {
          vertical-align: middle;
          &:after {
            display: none;
          }
        }
        .ant-table-selection-column + div {
          flex: 1 !important;
          width: auto !important;
        }
      `
    : css`
        .camera-meta-cell {
          padding-right: 18px !important;
        }

        .virtual-expand-table {
          td.recorder-name {
            text-align: left;
            padding-left: 20px !important;
          }
        }
      `}
`;

const CameraMetaRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
  padding-right: 18px;
  justify-items: end;
  box-sizing: border-box;
`;

const CameraMetaItem = styled.div`
  flex-direction: column;
  ${FLEX.FlexEndEnd};
  min-width: 0;

  .meta-label {
    font-size: ${calcRem(10)};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    opacity: 0.55;
  }

  .meta-value {
    font-size: ${calcRem(13)};
    line-height: 1.3;
    font-weight: 500;
    white-space: nowrap;
  }
`;

const CameraCountText = styled.span`
  display: inline-block;
  padding-right: 18px;
  box-sizing: border-box;
`;
